package com.schoolconnect.app.feature.notices

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.animation.core.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.list.*
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.feature.common.*
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.painterResource

@Composable
fun NoticesScreen(state: NoticesUiState, onEvent: (NoticesUiEvent) -> Unit, onBack: () -> Unit, shellRoleCode: String = "", onChangeRole: () -> Unit = onBack, onLogout: () -> Unit = {}) {
    var profileExpanded by remember { mutableStateOf(false) }
    var rowsMenu by remember { mutableStateOf(false) }
    var addVisible by remember { mutableStateOf(false) }
    var editing by remember { mutableStateOf<NoticeRowUi?>(null) }
    var viewing by remember { mutableStateOf<NoticeRowUi?>(null) }
    var deleteTarget by remember { mutableStateOf<NoticeRowUi?>(null) }
    val window = pageWindow(state.pageIndex, state.pageSize, state.totalCount)
    val pageItems = state.rows
    LaunchedEffect(state.saveGeneration) {
        if (state.saveGeneration > 0) {
            addVisible = false
            editing = null
        }
    }
    AdminListPageScaffold(title = "Notices", subtitle = "Create and manage notices for parents and school communication.", backgroundColor = AppBackground, titleColor = AppPageHeader, subtitleColor = AppPageSubtitle,
        headerActions = { ShellTopRightActions(shellRoleLabel(shellRoleCode), shellRoleSubtitle(shellRoleCode), shellAvatarInitials(shellRoleLabel(shellRoleCode)), profileExpanded, { profileExpanded = it }, onChangeRole, onLogout) },
        summaryCards = { if (state.isStatisticsLoading) NoticeSummaryCardsShimmer() else { NoticeMetric("Total Notices", state.totalNotices.toString(), "Across all notices", Res.drawable.file_spreadsheet, AppPrimary, AppPrimarySoft); NoticeMetric("Draft Notices", state.draftNotices.toString(), "Not yet published", Res.drawable.edit, Color(0xFFFF7A00), Color(0xFFFFF0E3)); NoticeMetric("Active Notices", state.activeNotices.toString(), "Currently active", Res.drawable.megaphone, AppSuccess, AppSuccessSoft); NoticeMetric("Expired Notices", state.expiredNotices.toString(), "Past expiry date", Res.drawable.ban, AppDanger, AppDangerSoft) } },
        toolbar = { NoticeFilterPanel(state, onEvent) },
        table = { modifier -> AdminListTableCard(modifier = modifier, minContentWidth = 1060.dp, header = { NoticeHeader(state, onEvent) }, body = { if (state.isLoading) AdminListTableShimmer(columns = listOf(AdminListHeaderColumn("Notice ID", .9f), AdminListHeaderColumn("Notice Title", 1.5f), AdminListHeaderColumn("Audience", 1.3f), AdminListHeaderColumn("Published On", 1f), AdminListHeaderColumn("Expiry Date", 1f), AdminListHeaderColumn("Status", .8f), AdminListHeaderColumn("Actions", .95f)), rowCount = state.pageSize) else if (pageItems.isEmpty()) Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { Text("No notices found", color = AppNavy, fontWeight = FontWeight.Bold) } else AdminListTableBody(items = pageItems, key = { it.id }, modifier = Modifier.fillMaxWidth().weight(1f)) { row -> NoticeRow(row, onView = { viewing = row }, onEdit = { editing = row; onEvent(NoticesUiEvent.ViewNotice(row.backendId)) }, onDelete = { deleteTarget = row }); if (row != pageItems.last()) HorizontalDivider(color = AppBorder) } }, footer = { AdminListFooter(pageText = pageFooterText(window, "notices"), pageIndex = window.pageIndex, pageCount = window.pageCount, onPageChanged = { onEvent(NoticesUiEvent.PageChanged(it)) }, pageSize = state.pageSize, rowsPerPageExpanded = rowsMenu, onRowsPerPageToggle = { rowsMenu = !rowsMenu }, onRowsPerPageDismiss = { rowsMenu = false }, onPageSizeChanged = { onEvent(NoticesUiEvent.PageSizeChanged(it)) }) }) },
        bottomAction = { Spacer(Modifier.weight(1f)); AdminListPrimaryActionButton(label = "Add Notice", icon = painterResource(Res.drawable.plus), onClick = { addVisible = true }, modifier = Modifier.padding(top = 4.dp)) })
    AddNoticeDialog(
        visible = addVisible || editing != null,
        initialNotice = editing,
        initialDetail = state.viewingNotice?.takeIf { detail -> editing?.id == detail.id },
        audienceOptions = state.audienceOptions,
        classTargetOptions = state.classTargetOptions,
        isLoadingDetails = editing != null && state.isViewingNotice,
        isSaving = state.isSaving,
        errorMessage = state.saveErrorMessage,
        onDismiss = { if (!state.isSaving) { addVisible = false; editing = null } },
        onSave = { draft -> editing?.let { onEvent(NoticesUiEvent.UpdateNotice(it.backendId, draft)) } ?: onEvent(NoticesUiEvent.CreateNotice(draft)) }
    )
    ViewNoticeDialog(
        notice = viewing,
        detail = state.viewingNotice,
        isLoading = state.isViewingNotice,
        errorMessage = state.viewNoticeError,
        onDismiss = { viewing = null },
        onView = { onEvent(NoticesUiEvent.ViewNotice(it)) }
    )
    deleteTarget?.let { target ->
        AppConfirmationDialog(
            onDismiss = { deleteTarget = null },
            title = "Delete Notice?",
            subtitle = "This notice will be moved out of active records.",
            bodyText = "Delete ${target.title}?",
            confirmLabel = "Delete",
            tone = AppConfirmationDialogTone.Danger,
            onConfirm = { deleteTarget = null; onEvent(NoticesUiEvent.DeleteNotice(target.backendId)) }
        )
    }
}

@Composable private fun RowScope.NoticeMetric(title: String, value: String, subtitle: String, icon: org.jetbrains.compose.resources.DrawableResource, accent: Color, bg: Color) = AdminListSummaryCard(title, value, subtitle, painterResource(icon), accent, bg, AppMuted, modifier = Modifier.weight(1f))

@Composable
private fun NoticeFilterPanel(state: NoticesUiState, onEvent: (NoticesUiEvent) -> Unit) {
    var customRangeVisible by remember { mutableStateOf(false) }
    val audienceOptions = state.audienceOptions
    val statusOptions = state.statusOptions
    val selectedAudienceLabel = audienceOptions.firstOrNull { it.code.equals(state.filters.audience, ignoreCase = true) }?.label
        ?: state.filters.audience.ifBlank { "All" }
    val selectedStatusLabel = statusOptions.firstOrNull { it.code.equals(state.filters.status, ignoreCase = true) }?.label
        ?: state.filters.status.ifBlank { "All" }
    AppCard(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
        Row(Modifier.fillMaxWidth().padding(16.dp), horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Bottom) {
            Column(Modifier.weight(1.45f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                Text("Search by Notice ID", color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                BoxWithConstraints(Modifier.fillMaxWidth()) {
                AdminListSearchField(state.searchQuery, { onEvent(NoticesUiEvent.SearchChanged(it)) }, "Search by Notice ID", width = maxWidth, height = 44.dp, surfaceColor = Color.White, borderColor = AppBorder, textColor = AppNavy)
                }
            }
            NoticeDropdown(
                "Audience",
                selectedAudienceLabel,
                audienceOptions.map { it.label },
                Modifier.weight(1f)
            ) { selected ->
                val selectedCode = audienceOptions.firstOrNull { it.label.equals(selected, ignoreCase = true) }?.code.orEmpty()
                onEvent(
                    NoticesUiEvent.FiltersChanged(
                        state.filters.copy(audience = selectedCode)
                    )
                )
            }
            NoticeDropdown("Status", selectedStatusLabel, statusOptions.map { it.label }, Modifier.weight(.9f)) {
                val selectedCode = statusOptions.firstOrNull { option -> option.label.equals(it, ignoreCase = true) }?.code.orEmpty()
                onEvent(NoticesUiEvent.FiltersChanged(state.filters.copy(status = selectedCode)))
            }
            NoticeDropdown("Published Date", state.filters.publishedDate, listOf("Today", "Last 7 Days", "This Month", "Custom Range"), Modifier.weight(1f)) {
                if (it.equals("Custom Range", ignoreCase = true)) {
                    customRangeVisible = true
                } else {
                    onEvent(NoticesUiEvent.FiltersChanged(state.filters.copy(publishedDate = it.ifBlank { "All" }, publishedDateFrom = "", publishedDateTo = "")))
                }
            }
            OutlinedButton(onClick = { onEvent(NoticesUiEvent.FiltersReset) }, modifier = Modifier.width(116.dp).height(44.dp), shape = RoundedCornerShape(8.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = AppPrimary)) { Icon(painterResource(Res.drawable.refresh_cw), null, Modifier.size(17.dp)); Spacer(Modifier.width(6.dp)); Text("Clear") }
        }
    }
    CustomNoticeDateRangeDialog(
        visible = customRangeVisible,
        initialFrom = state.filters.publishedDateFrom,
        initialTo = state.filters.publishedDateTo,
        onDismiss = { customRangeVisible = false },
        onApply = { from, to ->
            customRangeVisible = false
            onEvent(NoticesUiEvent.FiltersChanged(state.filters.copy(publishedDate = "Custom Range", publishedDateFrom = from, publishedDateTo = to)))
        }
    )
}

@Composable
private fun CustomNoticeDateRangeDialog(
    visible: Boolean,
    initialFrom: String,
    initialTo: String,
    onDismiss: () -> Unit,
    onApply: (String, String) -> Unit
) {
    if (!visible) return
    AppDialogDatePickerDialog(
        value = initialFrom,
        onDismiss = onDismiss,
        onDateSelected = {},
        colors = AppDialogDefaultColors,
        dateRangeSelection = true,
        dateRangeStartValue = initialFrom,
        dateRangeEndValue = initialTo,
        onDateRangeSelected = onApply
    )
}

@Composable
private fun NoticeDropdown(
    label: String,
    value: String,
    options: List<String>,
    modifier: Modifier,
    onSelected: (String) -> Unit
) = AppLabeledDropdownField(label, value, options, onSelected, modifier)

@Composable private fun NoticeHeader(state: NoticesUiState, onEvent: (NoticesUiEvent) -> Unit) { AdminListSortHeaderRow(listOf(AdminListSortColumn("Notice ID", .9f, state.sortField == NoticesSortField.ID, state.sortAscending) { onEvent(NoticesUiEvent.SortChanged(NoticesSortField.ID)) }, AdminListSortColumn("Notice Title", 1.5f, state.sortField == NoticesSortField.TITLE, state.sortAscending) { onEvent(NoticesUiEvent.SortChanged(NoticesSortField.TITLE)) }, AdminListSortColumn("Audience", 1.3f, state.sortField == NoticesSortField.AUDIENCE, state.sortAscending) { onEvent(NoticesUiEvent.SortChanged(NoticesSortField.AUDIENCE)) }, AdminListSortColumn("Published On", 1f, state.sortField == NoticesSortField.PUBLISHED_DATE, state.sortAscending) { onEvent(NoticesUiEvent.SortChanged(NoticesSortField.PUBLISHED_DATE)) }, AdminListSortColumn("Expiry Date", 1f, state.sortField == NoticesSortField.EXPIRY_DATE, state.sortAscending) { onEvent(NoticesUiEvent.SortChanged(NoticesSortField.EXPIRY_DATE)) }, AdminListSortColumn("Status", .8f, state.sortField == NoticesSortField.STATUS, state.sortAscending) { onEvent(NoticesUiEvent.SortChanged(NoticesSortField.STATUS)) },), actionsLabel = "Actions", actionsWeight = .95f, actionsAlignment = Alignment.Center) }

@Composable
private fun NoticeRow(row: NoticeRowUi, onView: () -> Unit, onEdit: () -> Unit, onDelete: () -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth().onViewDoubleClick(onView).heightIn(min = 52.dp).padding(horizontal = 24.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        listOf(row.id to .9f, row.title to 1.5f, row.audience to 1.3f, row.publishedOn to 1f, row.expiryDate to 1f).forEach { (value, weight) ->
            AppText(value, Modifier.weight(weight), style = MaterialTheme.typography.bodyMedium, color = AppNavy, fontWeight = FontWeight.Normal)
        }
        Box(Modifier.weight(.8f)) {
            Text(
                row.status,
                color = when (row.status) { "Draft" -> Color(0xFFD97706); "Expired" -> AppDanger; else -> AppSuccess },
                fontSize = 14.sp,
                fontWeight = FontWeight.Normal
            )
        }
        Box(Modifier.weight(.95f), contentAlignment = Alignment.Center) {
            AdminListActionIcons(
                onView = { expanded = false; onView() },
                onEdit = { expanded = false; if (row.status != "Expired") onEdit() },
                editEnabled = row.status != "Expired"
            ) {
                IconButton(onClick = { expanded = true }, modifier = Modifier.size(32.dp)) {
                    Icon(painterResource(Res.drawable.more_vertical), "More actions", tint = AppNavy, modifier = Modifier.size(16.dp))
                }
                AdminRowActionMenu(
                    listOf(
                        listOf(AdminRowActionItem("Delete", { expanded = false; onDelete() }, painterResource(Res.drawable.trash), labelColor = AppDanger, iconTint = AppDanger))
                    ),
                    { expanded = false },
                    expanded = expanded
                )
            }
        }
    }
}

@Composable
private fun RowScope.NoticeSummaryCardsShimmer() = AdminListSummaryCardsShimmer(
    cardCount = 4,
    cardHeight = 128.dp,
    cardShape = RoundedCornerShape(12.dp),
    contentPadding = PaddingValues(22.dp),
    iconSize = 70.dp,
    horizontalSpacing = 20.dp,
    titleWidth = 118.dp,
    titleHeight = 15.dp,
    valueWidth = 72.dp,
    valueHeight = 31.dp,
    subtitleWidth = 132.dp,
    subtitleHeight = 12.dp
)
